// src/components/OrganizationManagement/StatusModal.js
import React from 'react';
import PropTypes from 'prop-types';
import './StatusModal.css';

/**
 * Reusable status modal for showing loading/success/error states
 * 
 * Used by organization creation, update, authorization, and deletion flows
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {boolean} props.isLoading - Whether to show loading spinner
 * @param {boolean} props.isRefreshing - Optional refreshing state (shows different loading message)
 * @param {Object} props.result - Result object with type and message
 * @param {string} props.result.type - 'success', 'error', or 'warning'
 * @param {string} props.result.message - Message to display
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {string} props.loadingMessage - Message to show during loading
 * @param {string} props.refreshingMessage - Message to show during refresh
 * @param {Function} props.onSuccess - Optional callback after successful close
 * @param {string} props.successButtonText - Text for success button (default: "Continue")
 * @param {string} props.errorButtonText - Text for error button (default: "Close")
 * @param {Function} props.renderExtraContent - Optional function to render extra content below message
 */
const StatusModal = ({
  isOpen,
  isLoading,
  isRefreshing = false,
  result,
  onClose,
  loadingMessage = 'Processing...',
  refreshingMessage = 'Loading...',
  onSuccess,
  successButtonText = 'Continue',
  errorButtonText = 'Close',
  renderExtraContent,
}) => {
  if (!isOpen) return null;

  const handleClose = () => {
    if (result?.type === 'success' && onSuccess) {
      onSuccess(result);
    }
    onClose();
  };

  const handleOverlayClick = (e) => {
    // Only allow close if not loading/refreshing and has result
    if (!isLoading && !isRefreshing && result) {
      e.stopPropagation();
      handleClose();
    }
  };

  const isProcessing = isLoading || isRefreshing;
  const processingMessage = isRefreshing ? refreshingMessage : loadingMessage;

  return (
    <div
      className="status-modal-overlay"
      onClick={handleOverlayClick}
    >
      <div
        className="status-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        {isProcessing ? (
          <>
            <div className="status-modal-spinner"></div>
            <p className="status-modal-message">
              {processingMessage}
            </p>
          </>
        ) : result ? (
          <>
            <div className={`status-modal-icon ${result.type}`}>
              {result.type === 'success'
                ? '✓'
                : result.type === 'warning'
                ? '⚠'
                : '✕'}
            </div>
            <p className={`status-modal-message ${result.type}`}>
              {result.message}
            </p>
            {renderExtraContent && renderExtraContent(result)}
            <button
              className="status-modal-close-button"
              onClick={handleClose}
            >
              {result.type === 'success' ? successButtonText : errorButtonText}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
};

StatusModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isRefreshing: PropTypes.bool,
  result: PropTypes.shape({
    type: PropTypes.oneOf(['success', 'error', 'warning']).isRequired,
    message: PropTypes.string.isRequired,
  }),
  onClose: PropTypes.func.isRequired,
  loadingMessage: PropTypes.string,
  refreshingMessage: PropTypes.string,
  onSuccess: PropTypes.func,
  successButtonText: PropTypes.string,
  errorButtonText: PropTypes.string,
  renderExtraContent: PropTypes.func,
};

export default StatusModal;
